import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import {
  ComplianceTestOptionDto,
  RedesignReasonOptionDto,
  SpacerRedesignReasonsResponseDto,
  SpacerService
} from '../../services/spacer.service';

export interface AddNewSpacerResult {
  isRedesign: boolean;
  reasonTypeId: number | null;
  comments: string;
  complianceTestIds: number[];
}

@Component({
  selector: 'app-add-new-spacer-modal',
  imports: [CommonModule, FormsModule, MatDialogModule],
  templateUrl: './add-new-spacer-modal.component.html',
  styleUrls: ['./add-new-spacer-modal.component.css']
})
export class AddNewSpacerModalComponent implements OnInit {
  projectTypeId: number;
  projectId: number;
  projectName: string;
  slurries: any[];
  userEmail: string;
  serviceCompanyName: string;
  blendTest: boolean = false;

  isLoading = false;
  errorMessage = '';

  isRedesign = true;
  reasons: RedesignReasonOptionDto[] = [];
  selectedReasonTypeId: number | null = null;
  comments = '';
  selectedComplianceTestIds: number[] = [];

  constructor(
    private spacerService: SpacerService,
    private dialogRef: MatDialogRef<AddNewSpacerModalComponent>,
    private router: Router,
    @Inject(MAT_DIALOG_DATA) data: { 
      projectTypeId: number; 
      projectId: number; 
      projectName: string; 
      redesignByDefault?: boolean;
      slurries?: any[];
      userEmail?: string;
      serviceCompanyName?: string;
      blendTest?: boolean;
    }
  ) {
    this.projectTypeId = data?.projectTypeId ?? 2; // 2 for Spacer
    this.projectId = data?.projectId ?? 0;
    this.projectName = data?.projectName ?? '';
    this.isRedesign = data?.redesignByDefault ?? true;
    this.slurries = data?.slurries ?? [];
    this.userEmail = data?.userEmail ?? '';
    this.serviceCompanyName = data?.serviceCompanyName ?? '';
    this.blendTest = data?.blendTest ?? false;
  }

  ngOnInit(): void {
    this.loadRedesignReasons();
  }

  get selectedReason(): RedesignReasonOptionDto | undefined {
    return this.reasons.find(r => r.reasonTypeId === this.selectedReasonTypeId);
  }

  get availableComplianceTests(): ComplianceTestOptionDto[] {
    return this.selectedReason?.availableComplianceTests ?? [];
  }

  get requiresComplianceTests(): boolean {
    return this.selectedReason?.requiresComplianceTests ?? false;
  }

  get canSubmit(): boolean {
    if (!this.isRedesign) {
      return true;
    }

    if (!this.selectedReasonTypeId) {
      return false;
    }

    if (this.requiresComplianceTests && this.selectedComplianceTestIds.length === 0) {
      return false;
    }

    return true;
  }

  loadRedesignReasons(): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.spacerService.getRedesignReasons(this.projectTypeId).subscribe({
      next: (response: SpacerRedesignReasonsResponseDto) => {
        this.reasons = response.reasons ?? [];
        this.selectedReasonTypeId = null;
        this.selectedComplianceTestIds = [];
        this.isLoading = false;
      },
      error: () => {
        this.errorMessage = 'Unable to load redesign reasons. Please try again.';
        this.reasons = [];
        this.selectedReasonTypeId = null;
        this.selectedComplianceTestIds = [];
        this.isLoading = false;
      }
    });
  }

  onReasonChanged(): void {
    this.selectedComplianceTestIds = [];
  }

  onComplianceTestToggle(testTypeId: number, checked: boolean): void {
    if (checked) {
      if (!this.selectedComplianceTestIds.includes(testTypeId)) {
        this.selectedComplianceTestIds.push(testTypeId);
      }
      return;
    }

    this.selectedComplianceTestIds = this.selectedComplianceTestIds.filter(id => id !== testTypeId);
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    if (!this.canSubmit) {
      return;
    }

    const redesignData = {
      isRedesign: this.isRedesign,
      reasonTypeId: this.isRedesign ? this.selectedReasonTypeId : null,
      comments: this.comments?.trim() ?? '',
      complianceTestIds: this.isRedesign ? [...this.selectedComplianceTestIds] : []
    };

    // Close the dialog first
    this.dialogRef.close();

    // Navigate to create-spacer-info page
    this.router.navigate(['/projects-dashboard/create-spacer-info', this.projectId], {
      state: {
        projectId: this.projectId,
        projectType: this.projectTypeId,
        projectName: this.projectName,
        redesign: redesignData,
        slurries: this.slurries,
        userEmail: this.userEmail,
        serviceCompanyName: this.serviceCompanyName,
        blendTest: this.blendTest
      }
    });
  }
}
